import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, CheckCircle, Lightbulb, Loader2, RotateCcw, Target } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import {
  Exercise, LearningMode, ProgrammingLanguage, getPrebuiltExercise,
} from '@unity-learning/shared';
import { explainUnsupported, saveSimResult } from '../../services/api';
import { useUser } from '../../hooks/useUser';
import { useLearningMode } from '../../contexts/LearningModeContext';
import {
  compile, toCompilationIssues, toLlmHandoff, verifyFrameIndependence,
  DELTATIME_EXERCISE_SPEC, DELTATIME_STARTER_CODE,
} from '../../simulator';
import type { Program, SimError, SimFps, VerifyResult } from '../../simulator';
import type { CompilationIssue, IssueLine } from '../../utils/studentEditor';
import { StudentCodeEditor, StudentCodeEditorHandle } from './StudentCodeEditor';
import { NeonArena, NeonArenaHandle, ArenaStatus } from './NeonArena';
import { SimulatorConsole, ExplanationState } from './SimulatorConsole';
import { VerifyResultPanel, SaveState } from './VerifyResultPanel';

/**
 * Expérience complète du simulateur `Time.deltaTime` — rendue par ExercisePage quand
 * `exercise.type === 'interactive_sim'`.
 *
 * Gauche : énoncé (markdown) + éditeur étudiant (config identique à la page d'exercice) + console.
 * Droite : arène néon (5 / 30 / 60 FPS, Play / Stop / Reset, HUD) + résultat de la vérification.
 * Compilation et vérification : 100 % locales et déterministes (moteur G1). Le LLM n'intervient
 * que sur demande explicite (« Demander une explication ») via POST /api/chat/explain-unsupported.
 * La tentative est enregistrée par POST /api/exercises/:id/sim-result (progression nœud + KP).
 */

export interface SimulatorReviewAttempt {
  id: string;
  solution: string;
  isCorrect: boolean;
  timeSpent: number;
  submittedAt: string | Date;
  feedback?: { overallScore?: number; logicAnalysis?: string } | null;
}

interface SimulatorExerciseProps {
  exercise: Exercise;
  learningNodeId: string | null;
  mode?: 'new' | 'review';
  reviewAttempt?: SimulatorReviewAttempt | null;
}

const explainKey = (err: SimError) => `${err.kind}|${err.symbol ?? ''}|${err.line}|${err.message}`;

const markdownComponents = {
  h2: ({ children }: { children?: React.ReactNode }) => <h3 className="text-base font-semibold text-gray-900 dark:text-white mt-4 mb-2 first:mt-0">{children}</h3>,
  h3: ({ children }: { children?: React.ReactNode }) => <h4 className="text-sm font-semibold text-gray-900 dark:text-white mt-3 mb-1">{children}</h4>,
  strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-bold text-blue-800 dark:text-blue-300">{children}</strong>,
  code: ({ children }: { children?: React.ReactNode }) => (
    <code className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
  ),
  p: ({ children }: { children?: React.ReactNode }) => <p className="mb-3 last:mb-0">{children}</p>,
  ul: ({ children }: { children?: React.ReactNode }) => <ul className="list-disc pl-5 mb-3">{children}</ul>,
  ol: ({ children }: { children?: React.ReactNode }) => <ol className="list-decimal pl-5 mb-3">{children}</ol>,
  li: ({ children }: { children?: React.ReactNode }) => <li className="mb-1">{children}</li>,
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l-4 border-cyan-400 pl-3 text-gray-600 dark:text-gray-300 italic mb-3">{children}</blockquote>
  ),
};

export const SimulatorExercise: React.FC<SimulatorExerciseProps> = ({ exercise, learningNodeId, mode = 'new', reviewAttempt = null }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { learningMode: contextMode, programmingLanguage: contextLang } = useLearningMode();

  // Piège n°1 : le mode de l'exercice fait foi (catalogue), pas celui du contexte utilisateur.
  const prebuilt = getPrebuiltExercise(exercise.id);
  const learningMode = (prebuilt?.learningMode as LearningMode | undefined) ?? contextMode;
  const programmingLanguage = learningMode === LearningMode.UNITY_CSHARP ? ProgrammingLanguage.CSHARP : contextLang;

  const startingCode = exercise.startingCode || DELTATIME_STARTER_CODE;
  const initialCode = mode === 'review' && reviewAttempt?.solution ? reviewAttempt.solution : startingCode;

  const [code, setCode] = useState(initialCode);
  const [fps, setFps] = useState<SimFps>(60);
  const [issues, setIssues] = useState<CompilationIssue[] | null>(null);
  const [simErrors, setSimErrors] = useState<SimError[]>([]);
  const [compiledOk, setCompiledOk] = useState(false);
  const [program, setProgram] = useState<Program | null>(null);
  const [arenaStatus, setArenaStatus] = useState<ArenaStatus>('idle');
  const [explanation, setExplanation] = useState<ExplanationState | null>(null);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
  const [saveState, setSaveState] = useState<SaveState | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showHints, setShowHints] = useState(false);

  const editorRef = useRef<StudentCodeEditorHandle>(null);
  const arenaRef = useRef<NeonArenaHandle>(null);
  const startTimeRef = useRef(Date.now());
  const explainCacheRef = useRef(new Map<string, string>());

  useEffect(() => {
    setCode(initialCode);
    startTimeRef.current = Date.now();
  }, [initialCode, exercise.id]);

  const issueLines = useMemo<IssueLine[]>(
    () => (issues ?? []).filter((i) => i.line !== null).map((i) => ({ line: i.line as number, severity: i.severity })),
    [issues]
  );

  const applyErrors = useCallback((errors: SimError[], warnings: string[]) => {
    setIssues(toCompilationIssues(errors, warnings));
    setSimErrors(errors);
  }, []);

  const handleCompileAndPlay = useCallback(() => {
    const result = compile(code);
    if (!result.ok) {
      applyErrors(result.errors, result.warnings);
      setCompiledOk(false);
      setProgram(null);
      arenaRef.current?.reset();
      return;
    }
    applyErrors([], result.warnings);
    setCompiledOk(true);
    setProgram(result.program);
    arenaRef.current?.play(result.program);
  }, [applyErrors, code]);

  const handleRuntimeError = useCallback((err: SimError) => {
    setIssues((prev) => [...toCompilationIssues([err]), ...(prev ?? []).filter((i) => i.severity === 'warning')]);
    setSimErrors([err]);
  }, []);

  const handleIssueClick = useCallback((line: number) => {
    editorRef.current?.scrollToLine(line);
  }, []);

  const handleExplain = useCallback(async (err: SimError) => {
    if (!user) return;
    const key = explainKey(err);
    const cached = explainCacheRef.current.get(key);
    if (cached) {
      setExplanation({ key, status: 'done', text: cached, symbol: err.symbol });
      return;
    }
    setExplanation({ key, status: 'loading', symbol: err.symbol });
    try {
      const handoff = toLlmHandoff(err, code);
      const explanationText = await explainUnsupported({
        userId: user.id,
        exerciseId: exercise.id,
        learningMode,
        programmingLanguage,
        handoff,
        studentCode: code,
      });
      explainCacheRef.current.set(key, explanationText);
      setExplanation({ key, status: 'done', text: explanationText, symbol: err.symbol });
    } catch (error) {
      setExplanation({ key, status: 'error', symbol: err.symbol, error: error instanceof Error ? error.message : 'Le Coach est indisponible.' });
    }
  }, [code, exercise.id, learningMode, programmingLanguage, user]);

  const handleVerify = useCallback(async () => {
    if (!user) return;
    setIsVerifying(true);
    setSaveState(null);
    const result = verifyFrameIndependence(code, DELTATIME_EXERCISE_SPEC.verify);
    setVerifyResult(result);
    if (result.errors.length > 0) {
      applyErrors(result.errors, result.warnings);
      setCompiledOk(false);
    } else if (issues === null) {
      applyErrors([], result.warnings);
      setCompiledOk(true);
    }
    // Défilement vers le résultat une fois rendu
    window.setTimeout(() => document.getElementById('sim-verify-result')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);

    setSaveState({ status: 'saving' });
    try {
      const timeSpent = Math.max(0, Math.floor((Date.now() - startTimeRef.current) / 1000));
      const outcome = await saveSimResult(exercise.id, {
        userId: user.id,
        solution: code,
        learningNodeId: learningNodeId ?? prebuilt?.nodeId ?? null,
        result,
        timeSpent,
        learningMode,
        programmingLanguage,
      });
      if (!outcome) throw new Error('Réponse vide du serveur');
      setSaveState({ status: 'saved', score: outcome.score, isCorrect: outcome.isCorrect, missingKpCodes: outcome.missingKpCodes });
    } catch (error) {
      setSaveState({ status: 'error', error: error instanceof Error ? error.message : 'erreur réseau' });
    } finally {
      setIsVerifying(false);
    }
  }, [applyErrors, code, exercise.id, issues, learningMode, learningNodeId, prebuilt?.nodeId, programmingLanguage, user]);

  const handleResetCode = useCallback(() => {
    setCode(startingCode);
    setIssues(null);
    setSimErrors([]);
    setCompiledOk(false);
    setProgram(null);
    setExplanation(null);
    setVerifyResult(null);
    setSaveState(null);
    arenaRef.current?.reset();
  }, [startingCode]);

  const backTo = mode === 'review' ? '/progression' : '/';
  const codeLanguage = programmingLanguage === ProgrammingLanguage.CSHARP ? 'C#' : programmingLanguage;
  const reviewDate = reviewAttempt ? new Date(reviewAttempt.submittedAt) : null;

  return (
    <div className="space-y-4" data-testid="simulator-exercise">
      {/* En-tête */}
      <div className="glass dark:glass-dark rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => navigate(backTo)}
            className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {mode === 'review' ? 'Retour à la progression' : 'Retour au parcours'}
          </button>
          <div className="flex items-center space-x-3 text-sm text-gray-500 dark:text-gray-400">
            <span className="px-2 py-1 bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200 rounded-full text-xs font-medium">
              Simulateur
            </span>
            <span className="flex items-center"><Clock className="w-4 h-4 mr-1" />{exercise.estimatedTimeMinutes || 20} min</span>
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">{exercise.difficulty}</span>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
          {mode === 'review' && '📋 Révision : '}{exercise.title}
        </h1>
        {exercise.description && (
          <div className="text-gray-600 dark:text-gray-300">
            <ReactMarkdown components={{ strong: markdownComponents.strong, code: markdownComponents.code, p: ({ children }) => <p className="mb-0">{children}</p> }}>
              {exercise.description}
            </ReactMarkdown>
          </div>
        )}
        {mode === 'review' && reviewAttempt && (
          <div className={`mt-3 rounded-lg border px-3 py-2 text-sm ${reviewAttempt.isCorrect ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200' : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'}`} data-testid="review-banner">
            <div className="font-medium">
              Tentative du {reviewDate ? reviewDate.toLocaleDateString('fr-FR') : '—'} — {reviewAttempt.isCorrect ? 'Réussie' : 'Échouée'}
              {reviewAttempt.feedback?.overallScore !== undefined && ` · ${reviewAttempt.feedback.overallScore}/100`}
            </div>
            {reviewAttempt.feedback?.logicAnalysis && (
              <div className="mt-1 opacity-90">
                <ReactMarkdown components={{ strong: markdownComponents.strong, code: markdownComponents.code, p: ({ children }) => <p className="mb-0">{children}</p> }}>
                  {reviewAttempt.feedback.logicAnalysis}
                </ReactMarkdown>
              </div>
            )}
            <div className="mt-1 text-xs opacity-75">Ton code de l'époque est chargé : tu peux le rejouer, le modifier et vérifier à nouveau (une nouvelle tentative sera enregistrée).</div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        {/* Colonne gauche : énoncé + éditeur + console */}
        <div className="space-y-4 min-w-0">
          <div className="glass dark:glass-dark rounded-xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <Target className="w-5 h-5 mr-2" /> Énoncé
            </h2>
            <div className="text-gray-800 dark:text-gray-200 leading-relaxed">
              <ReactMarkdown components={markdownComponents}>{exercise.prompt}</ReactMarkdown>
            </div>
            {(exercise.successCriteria?.length || exercise.hints?.length) ? (
              <div className="mt-4 space-y-3">
                {exercise.successCriteria && exercise.successCriteria.length > 0 && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-1 flex items-center"><CheckCircle className="w-4 h-4 mr-1" /> Critères de réussite</h4>
                    <ul className="list-disc pl-5 text-sm text-green-800 dark:text-green-200 space-y-0.5">
                      {exercise.successCriteria.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                )}
                {exercise.hints && exercise.hints.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <button
                      type="button"
                      onClick={() => setShowHints((v) => !v)}
                      className="text-sm font-semibold text-amber-800 dark:text-amber-300 flex items-center"
                    >
                      <Lightbulb className="w-4 h-4 mr-1" /> {showHints ? 'Masquer les indices' : `Afficher les indices (${exercise.hints.length})`}
                    </button>
                    {showHints && (
                      <ol className="list-decimal pl-5 mt-2 text-sm text-amber-900 dark:text-amber-100 space-y-1">
                        {exercise.hints.map((h, i) => <li key={i}>{h}</li>)}
                      </ol>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="glass dark:glass-dark rounded-xl p-6">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Ton code</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleResetCode}
                  className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors inline-flex items-center"
                >
                  <RotateCcw className="w-4 h-4 mr-1" /> Réinitialiser le code
                </button>
                <button
                  type="button"
                  onClick={handleVerify}
                  disabled={isVerifying || !user}
                  data-testid="verify-button"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isVerifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                  Vérifier mon code
                </button>
              </div>
            </div>

            <StudentCodeEditor
              ref={editorRef}
              value={code}
              onChange={setCode}
              language={codeLanguage}
              issueLines={issueLines}
            />

            <SimulatorConsole
              issues={issues}
              errors={simErrors}
              compiledOk={compiledOk}
              onIssueClick={handleIssueClick}
              onExplain={handleExplain}
              explanation={explanation}
              explainKey={explainKey}
            />
          </div>
        </div>

        {/* Colonne droite : arène + résultat */}
        <div className="space-y-4 min-w-0">
          <div className="glass dark:glass-dark rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Arène</h2>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {arenaStatus === 'running' ? 'Simulation en cours' : arenaStatus === 'stopped' ? 'En pause' : arenaStatus === 'error' ? 'Erreur' : 'Prête'}
              </span>
            </div>
            <NeonArena
              ref={arenaRef}
              fps={fps}
              onFpsChange={setFps}
              onRequestPlay={handleCompileAndPlay}
              onRuntimeError={handleRuntimeError}
              onStatusChange={setArenaStatus}
              hasProgram={program !== null}
            />
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Change la cadence pendant le jeu : sans <code className="font-mono">Time.deltaTime</code>, la « distance / s » du HUD est multipliée par 12 entre 5 et 60 FPS.
            </p>
          </div>

          {verifyResult && (
            <VerifyResultPanel result={verifyResult} save={saveState} onRestart={handleResetCode} backTo={backTo} />
          )}
        </div>
      </div>
    </div>
  );
};

export default SimulatorExercise;
